import type {
  CoverageClient,
  CoverageFilters,
  CoveragePage,
  CoverageSort,
  CoverageSummary,
  SavedView,
} from '@fruuit/shared'
import { keepPreviousData } from '@tanstack/react-query'
import { ApiError, api } from '../../api/client'
import { useApiMutation, useApiQuery } from '../../api/hooks'

/** Filter keys the URL carries for the coverage table (PORT-002 + label + mapping). */
export const FILTER_KEYS = [
  'query',
  'portfolio',
  'finding',
  'theme',
  'sector',
  'country',
  'engagement_status',
  'label',
  'mapping',
] as const
export type FilterKey = (typeof FILTER_KEYS)[number]
export type FilterState = Partial<Record<FilterKey, string>>

/** Saved views accept only the report filter keys (RPT-002 / PORT-006). */
export const SAVED_VIEW_KEYS = [
  'portfolio',
  'finding',
  'theme',
  'country',
  'sector',
  'engagement_status',
  'query',
] as const

export function filtersFromParams(params: URLSearchParams): FilterState {
  const out: FilterState = {}
  for (const key of FILTER_KEYS) {
    const value = params.get(key)
    if (value) out[key] = value
  }
  return out
}

export function coverageKey(org: string | null, ...rest: unknown[]) {
  return ['coverage', org ?? 'own', ...rest]
}

export function useCoverage(options: {
  org: string | null
  filters: FilterState
  sort: CoverageSort
  dir: 'asc' | 'desc'
  cursor: string | null
  limit?: number
  enabled?: boolean
}) {
  const { org, filters, sort, dir, cursor, limit = 50, enabled = true } = options
  return useApiQuery<CoveragePage>(
    coverageKey(org, 'rows', filters, sort, dir, cursor, limit),
    '/coverage',
    {
      query: { ...filters, org, sort, dir, cursor, limit },
      placeholderData: keepPreviousData,
      enabled,
    },
  )
}

export function useCoverageSummary(org: string | null, filters: FilterState, enabled = true) {
  return useApiQuery<CoverageSummary>(coverageKey(org, 'summary', filters), '/coverage/summary', {
    query: { ...filters, org },
    placeholderData: keepPreviousData,
    enabled,
  })
}

export function useCoverageClients(enabled = true) {
  return useApiQuery<{ items: CoverageClient[] }>(['coverage', 'clients'], '/coverage/clients', {
    enabled,
  })
}

// --- Saved views (owned by the engagement lane; handled gracefully if absent) ---

export function useSavedViews() {
  return useApiQuery<{ items: SavedView[] }>(['saved-views', 'portfolio'], '/saved-views', {
    query: { scope: 'portfolio', limit: 100 },
  })
}

export function useSaveView() {
  return useApiMutation(
    (vars: { name: string; filters: CoverageFilters | FilterState }, { requestId }) =>
      api.post<SavedView>(
        '/saved-views',
        { name: vars.name, scope: 'portfolio', filters: vars.filters },
        { requestId },
      ),
    { invalidate: [['saved-views']] },
  )
}

/** Only the keys a saved view may store, without empties. */
export function savedViewFilters(filters: FilterState): Record<string, string> {
  const out: Record<string, string> = {}
  for (const key of SAVED_VIEW_KEYS) {
    const value = filters[key]
    if (value) out[key] = value
  }
  return out
}

// --- Coverage CSV export (served by the reports lane) ------------------------

/**
 * Download `/exports/coverage.csv` with the current filters (PORT-022). The
 * export route belongs to the reports lane; until it exists this reports a
 * friendly error instead of navigating to a JSON 404.
 */
export async function downloadCoverageCsv(org: string | null, filters: FilterState): Promise<void> {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value)
  if (org) params.set('org', org)
  const url = `/api/v1/exports/coverage.csv${params.size ? `?${params}` : ''}`
  const response = await fetch(url, { credentials: 'include', headers: { Accept: 'text/csv' } })
  if (!response.ok) {
    if (response.status === 404) {
      throw new ApiError({
        status: 404,
        code: 'not_found',
        message: 'The coverage export is not available yet. Try again after the next release.',
      })
    }
    throw new ApiError({
      status: response.status,
      code: 'export_failed',
      message: 'The export could not be prepared. Nothing was downloaded.',
    })
  }
  const blob = await response.blob()
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'fruuit-coverage.csv'
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = name
  document.body.append(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(link.href), 1000)
}
